import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Button,
  Checkbox,
  Col,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Tooltip,
  FormInstance,
} from "antd";
import { EditOutlined, InfoCircleOutlined, RightOutlined } from "@ant-design/icons";
import { capitalizeString, groupAndSortByKey } from "../../common/utils/helpers";
import TextArea from "antd/lib/input/TextArea";
import { Application, EditingValue, Permission } from "./types/apiClients";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import ActionButton from "stories/components/ActionButton/ActionButton";

const { Option } = Select;

export interface EditClientApiProps {
  visible: boolean;
  hasPermissions: boolean;
  form: FormInstance;
  editFormRef: React.Ref<FormInstance<any>>;
  permissionsList: Permission[];
  applicationsList: Application[];
  editing: EditingValue;
  closeEditDrawer: (e?: React.MouseEvent | React.KeyboardEvent) => void;
  onFinishEditing: (values: Record<string, any>) => void;
  onOpen?: () => void;
}

const EditClientApi: React.FC<EditClientApiProps> = ({
  visible,
  form,
  editFormRef,
  permissionsList,
  applicationsList,
  editing,
  closeEditDrawer,
  onFinishEditing,
  onOpen,
  hasPermissions
}) => {

  const [selectedCA, setSelectedCA] = useState<Set<string>>(new Set());
  useDrawerScrollMargin(editing?.editingRow?._id, visible);
  useEffect(() => {
    const row = editing.editingRow;
    if (!row) return;

    const customerApps = Array.isArray(row.customerApplicationId)
      ? row.customerApplicationId
      : [row.customerApplicationId];
    setSelectedCA(new Set(customerApps));
  }, [editing.editingRow, visible]);

  const permissionMap = useMemo(() => {
    const map = new Map<string, Permission[]>();
    permissionsList.forEach((perm) => {
      if (!map.has(perm.customerApplication)) {
        map.set(perm.customerApplication, []);
      }
      map.get(perm.customerApplication)!.push(perm);
    });
    return map;
  }, [permissionsList]);

  const initialValues = useMemo(() => {
    const row = editing.editingRow;
    if (!row) return {};

    const vals: Record<string, any> = {
      nameValue: row.name,
      descriptionValue: row.description,
      isActiveValue: row.isActive,
      customerApplicationsValue: Array.isArray(row.customerApplicationId)
        ? row.customerApplicationId
        : [row.customerApplicationId],
    };
    row.permissions.forEach((p) => {
      Object.entries(p).forEach(([key, value]) => {
        if (key !== "permissionId" && value) {
          vals[`permissions~${key}-${p.permissionId}`] = value;
        }
      });
    });
    return vals;
  }, [editing.editingRow]);


  useEffect(() => {
    if (visible && initialValues && Object.keys(initialValues).length) {
      form.setFieldsValue(initialValues);
    }
  }, [visible, initialValues, form]);

  const handleCAChange = useCallback((value: string | string[]) => {
    const newSet = new Set(Array.isArray(value) ? value : [value]);
    setSelectedCA(newSet);
  }, []);

  const columns = useMemo(() => {
    type PermKey = "read" | "create" | "edit" | "delete";
    const base = [
      {
        title: "",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "",
        dataIndex: "description",
        key: "description",
        width: 10,
        render: (_: any, record: Permission) => (
          <Tooltip
            title={record.description}
            color="var(--tertiary-color)"
            placement="left"
          >
            <InfoCircleOutlined style={{ color: "var(--tertiary-color)" }} />
          </Tooltip>
        ),
      },
    ];

    const permCols = (["read", "create", "edit", "delete"] as PermKey[]).map(
      (key) => ({
        title: capitalizeString(key),
        dataIndex: key,
        key,
        width: 60,
        render: (_: any, record: Permission) => {
          const fieldName = `permissions~${key}-${record.id}`;
          const supportsKey = `supports${capitalizeString(
            key
          )}` as keyof Permission as keyof Permission;
          const supports = record[supportsKey] as boolean;

          return (
            <Form.Item
              name={fieldName}
              key={fieldName}
              noStyle
              valuePropName="checked"
            >
              {supports ? (
                <Checkbox name={key} />
              ) : (
                <Tooltip
                  title="This permission does not support this change."
                  placement="left"
                >
                  <Checkbox name={key} disabled />
                </Tooltip>
              )}
            </Form.Item>
          );
        },
      })
    );

    return [...base, ...permCols];
  }, []);


  const renderedPermissionTables = useMemo(
    () =>
      Array.from(selectedCA).flatMap((ca) => {
        const perms = permissionMap.get(ca) || [];
        const grouped = groupAndSortByKey(perms, "category");
        const app = applicationsList.find((a) => a._id === ca);
        if (!app) return [];
        return Object.entries(grouped).map(([category, list]) => (
          <Table<Permission>
            key={`${ca}-${category}`}
            columns={columns}
            //@ts-ignore
            dataSource={[...list].sort((a, b) => a.name.localeCompare(b.name))}
            title={() => (
              <Tooltip
                title={`${capitalizeString(
                  category
                )} category is part of ${capitalizeString(
                  app.name
                )} application.`}
                color="var(--tertiary-color)"
                placement="left"
              >
                <span style={{ cursor: "help" }}>
                  {app.name}
                  <RightOutlined
                    style={{
                      color: "var(--primary-color)",
                      margin: "0 3px",
                      fontSize: 12,
                    }}
                  />
                  {category}
                </span>
              </Tooltip>
            )}
            pagination={false}
            className="role-table"
          />
        ));
      }),
    [selectedCA, permissionMap, applicationsList, columns]
  );

  const handleClose = (
    e?: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent
  ) => {
    closeEditDrawer(e);
    setSelectedCA(new Set());
    form.resetFields();
  };

  return (
    <>
      <ActionButton
        onClick={onOpen}
        text="Edit Api Client"
        type="primary"
        disabled={!hasPermissions}
        icon={<EditOutlined />}
      />
      <Drawer
        title="Edit Api Client"
        placement="right"
        onClose={(e) => handleClose()}
        open={visible}
        width={550}
        closable
        className="manage-pages-drawer edit-api-client-drawer"
        forceRender
      >
        <Form
          form={form}
          ref={editFormRef}
          layout="vertical"
          onFinish={(vals) => {
            if (!editing.editingRow) return;
            onFinishEditing({ ...vals, _id: editing.editingRow._id });
          }}
          name="editApiClientForm"
          initialValues={initialValues}
          autoComplete="off"
          className="edit-client-api-form"
        >
          <Form.Item
            name="nameValue"
            label="Name"
            rules={[{ required: true, message: "Please type the name" }]}
          >
            <Input placeholder="Ex: DEVOPS" />
          </Form.Item>
          <Form.Item
            name="descriptionValue"
            label="Description"
            rules={[{ required: true, message: "Please type the description" }]}
          >
            <TextArea rows={4} placeholder="Project Manager API Client for ABC" />
          </Form.Item>
          <Row>
            <Col span={8}>
              <Form.Item
                name="isActiveValue"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="customerApplicationsValue"
            label="Customer Application"
            rules={[{ required: true, message: "Select an application" }]}
          >
            <Select
              placeholder="Select application"
              onChange={handleCAChange}
              defaultValue={initialValues.customerApplicationsValue}
            >
              {applicationsList
                .filter((a) => a.active)
                .map((app) => (
                  <Option key={app._id} value={app._id}>
                    {capitalizeString(app.name)}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          {selectedCA.size === 0 ? (
            <p>Please select customer application first</p>
          ) : (
            <Form.Item label="Permissions">{renderedPermissionTables}</Form.Item>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update Client Api
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default EditClientApi;
